/**
 * Graduates — public "Certificate Wall" celebrating the latest FDAcademy graduates.
 *
 * Route: /graduates (public). Fetches /api/graduates and renders a filter-able
 * grid of graduate tiles. Each tile shows the branded 1200×630 share-card
 * image and links to the public verify page. Doubles as social proof for
 * prospective learners and a quiet flex for graduates.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import {
  Award, GraduationCap, Sparkles, Trophy, ArrowUpRight, Loader2,
  ShieldCheck,
} from 'lucide-react';
import BrandMark from '../components/BrandMark';
import KudosButton from '../components/KudosButton';
import useDocumentMeta from '../hooks/useDocumentMeta';

const KINDS = [
  { id: 'all',         label: 'All graduates',   icon: GraduationCap },
  { id: 'course',      label: 'Completions',     icon: Award },
  { id: 'designation', label: 'Designations',    icon: Trophy },
  { id: 'specialty',   label: 'Specialties',     icon: Sparkles },
];

const KIND_META = {
  course:      { pill: 'Completion',    accent: 'bg-blue-500/15 text-blue-600 border-blue-500/30' },
  designation: { pill: 'Designation',   accent: 'bg-amber-500/15 text-amber-700 border-amber-500/30' },
  specialty:   { pill: 'Specialty',     accent: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' },
};

const fmtIssued = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch (_) {
    return iso;
  }
};

const GraduateTile = ({ grad, API, kudos, isTopAmbassador, ambassadorRank }) => {
  const navigate = useNavigate();
  const meta = KIND_META[grad.kind] || KIND_META.course;
  const displayName = grad.last_initial
    ? `${grad.first_name} ${grad.last_initial}`
    : grad.first_name;
  const k = kudos?.[grad.cert_number] || { count: 0, given: false };
  const ringClass = isTopAmbassador
    ? 'ring-4 ring-amber-400/70 shadow-[0_0_0_2px_rgba(245,184,65,0.15)]'
    : '';
  return (
    <div
      className={`group text-left rounded-2xl overflow-hidden bg-card border border-border hover:border-primary/60 transition-all hover:-translate-y-0.5 hover:shadow-lg relative ${ringClass}`}
      data-testid={isTopAmbassador ? `top-ambassador-tile-${grad.cert_number}` : undefined}
    >
      {isTopAmbassador && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
          <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest bg-amber-400 text-navy shadow-lg">
            <Trophy className="w-3 h-3" /> Top Ambassador{ambassadorRank ? ` · #${ambassadorRank}` : ''}
          </span>
        </div>
      )}
      <button
        type="button"
        onClick={() => navigate(`/certificate/${grad.cert_number}`)}
        data-testid={`graduate-tile-${grad.cert_number}`}
        className="block w-full text-left"
      >
        <div className="relative aspect-[1200/630] bg-navy-spotlight overflow-hidden">
          <img
            src={`${API}/verify/${grad.cert_number}/share-card.png`}
            alt={`${displayName} — ${grad.title}`}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.opacity = '0.15'; }}
          />
          <div className="absolute top-3 left-3">
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest border ${meta.accent} bg-background/90 backdrop-blur`}>
              {meta.pill}
            </span>
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-heading text-lg font-extrabold text-foreground truncate">
                {displayName}
              </div>
              <div className="text-sm text-foreground/70 truncate">{grad.title}</div>
              {grad.event_program && (
                <div className="text-xs text-muted-foreground truncate mt-0.5">{grad.event_program}</div>
              )}
            </div>
            <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
          </div>
          <div className="mt-3 flex items-center justify-between text-[11px]">
            <span className="text-muted-foreground uppercase tracking-widest font-bold">
              {fmtIssued(grad.issued_at)}
            </span>
            <span className="font-mono text-foreground/60">{grad.cert_number}</span>
          </div>
        </div>
      </button>
      <div className="px-4 pb-4 -mt-1 flex items-center justify-between">
        <KudosButton
          targetType="graduate_cert"
          targetId={grad.cert_number}
          initialCount={k.count}
          initialGiven={k.given}
          size="sm"
        />
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
          {k.count > 0 ? `${k.count} cheer${k.count === 1 ? '' : 's'}` : 'be the first to cheer'}
        </span>
      </div>
    </div>
  );
};

const Graduates = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const [kudos, setKudos] = useState({});
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbLoading, setLbLoading] = useState(true);

  useDocumentMeta({
    title: 'The Graduate Wall — Freeze Dry Academy',
    description: 'See the latest freeze-dry operators, designations, and specialties earned by the Freeze Dry Academy community. Every certificate is signed and independently verifiable.',
    ogType: 'website',
  });

  useEffect(() => {
    let cancelled = false;
    setLbLoading(true);
    axios.get(`${API}/referrals/leaderboard?limit=10`)
      .then((res) => { if (!cancelled) setLeaderboard(res.data?.entries || []); })
      .catch(() => { if (!cancelled) setLeaderboard([]); })
      .finally(() => { if (!cancelled) setLbLoading(false); });
    return () => { cancelled = true; };
  }, [API]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ limit: '48' });
    if (filter !== 'all') params.set('kind', filter);
    axios.get(`${API}/graduates?${params.toString()}`)
      .then((res) => { if (!cancelled) setRows(res.data.graduates || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [API, filter]);

  // Batch-fetch kudos summary whenever the list of visible certs changes.
  useEffect(() => {
    let cancelled = false;
    const ids = rows.map((r) => r.cert_number).filter(Boolean);
    if (!ids.length) { setKudos({}); return; }
    axios.get(`${API}/kudos/summary`, {
      params: { target_type: 'graduate_cert', target_ids: ids.join(',') },
    })
      .then((res) => { if (!cancelled) setKudos(res.data?.items || {}); })
      .catch(() => { if (!cancelled) setKudos({}); });
    return () => { cancelled = true; };
  }, [API, rows]);

  const counts = useMemo(() => ({
    all:         rows.length,
  }), [rows]);

  return (
    <div className="min-h-screen bg-background" data-testid="graduates-page">
      {/* Public top strip */}
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-6 h-14 flex items-center justify-between">
          <BrandMark size="sm" onClick={() => navigate('/')} testId="graduates-brand" />
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate('/why-us')}
              className="hidden sm:inline-flex rounded-full font-semibold"
              data-testid="graduates-nav-why-us"
            >
              Why us
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/pricing')}
              className="hidden sm:inline-flex rounded-full font-semibold"
              data-testid="graduates-nav-pricing"
            >
              Pricing
            </Button>
            {user ? (
              <Button onClick={() => navigate('/dashboard')} className="rounded-full font-bold bg-primary hover:bg-primary/90" data-testid="graduates-nav-dashboard">
                Dashboard
              </Button>
            ) : (
              <Button onClick={() => navigate('/login')} className="rounded-full font-bold bg-primary hover:bg-primary/90" data-testid="graduates-nav-login">
                Log in
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-navy-spotlight text-background relative overflow-hidden">
        <div className="container mx-auto px-6 py-16 relative z-10">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/20 border border-amber-500/40 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-amber-300">
            <GraduationCap className="w-3.5 h-3.5" /> The Graduate Wall
          </span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-3xl">
            Meet the operators <span className="text-amber-brand">shipping real batches.</span>
          </h1>
          <p className="mt-4 max-w-2xl text-background/70 text-base">
            Every certificate on this wall was earned by a working freeze-drier. Time-verified coursework, HMAC-signed credentials, and a public verify link — the real proof of practice in our community.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 px-3 py-1.5 text-xs font-bold text-emerald-300">
              <ShieldCheck className="w-3.5 h-3.5" /> Signature-verified
            </div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-background/10 border border-background/20 px-3 py-1.5 text-xs font-bold text-background/80">
              Public verify · QR-scannable
            </div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-background/10 border border-background/20 px-3 py-1.5 text-xs font-bold text-background/80">
              Add-to-LinkedIn ready
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="container mx-auto px-6 py-8">
        {/* Referral leaderboard — top 10 ambassadors */}
        <section className="mb-8" data-testid="referral-leaderboard-section">
          <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
            <div>
              <div className="eyebrow text-primary inline-flex items-center gap-1.5">
                <Trophy className="w-3 h-3" /> Top ambassadors
              </div>
              <h2 className="font-heading text-2xl font-extrabold mt-1">Loudest referrers this cycle</h2>
              <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                Every 5 signups earned via a member&rsquo;s share link or certificate unlocks another month of Pro. Here&rsquo;s who&rsquo;s been spreading the word.
              </p>
            </div>
            {user && (
              <Button
                onClick={() => navigate('/portal')}
                variant="outline"
                className="rounded-full font-bold"
                data-testid="graduates-my-referral-link-btn"
              >
                Get my referral link <ArrowUpRight className="ml-1.5 w-3.5 h-3.5" />
              </Button>
            )}
          </div>
          {lbLoading ? (
            <div className="py-8 text-center text-muted-foreground text-sm" data-testid="leaderboard-loading">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Loading the leaderboard…
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="pro-card p-6 text-center text-sm text-muted-foreground" data-testid="leaderboard-empty">
              Be the first — share your referral link from <button type="button" onClick={() => navigate(user ? '/portal' : '/login')} className="text-primary font-bold hover:underline">your portal</button> and land on this wall.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="leaderboard-grid">
              {leaderboard.map((e) => (
                <div
                  key={`${e.rank}-${e.display_name}`}
                  className={`pro-card p-4 relative ${e.rank <= 3 ? 'border-amber-500/50' : ''}`}
                  data-testid={`leaderboard-entry-${e.rank}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center font-heading font-extrabold text-sm flex-shrink-0
                      ${e.rank === 1 ? 'bg-amber-500/20 text-amber-700' : e.rank === 2 ? 'bg-slate-400/20 text-slate-600' : e.rank === 3 ? 'bg-orange-500/20 text-orange-700' : 'bg-muted text-muted-foreground'}`}>
                      #{e.rank}
                    </div>
                    <div className="min-w-0">
                      <div className="font-heading text-sm font-extrabold truncate">{e.display_name}</div>
                      {e.country && <div className="text-[11px] text-muted-foreground truncate">{e.country}</div>}
                    </div>
                  </div>
                  <div className="mt-3 flex items-baseline gap-1.5">
                    <span className="font-heading text-2xl font-extrabold text-foreground">{e.total_referrals}</span>
                    <span className="text-[11px] text-muted-foreground">signups</span>
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    Cert {e.cert_referrals} · Link {e.direct_referrals}
                  </div>
                  {e.milestone_tier && (
                    <div
                      className={`mt-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest
                        ${e.milestone_tier.key === 'elite'
                          ? 'bg-fuchsia-500/10 text-fuchsia-700 border-fuchsia-500/40'
                          : e.milestone_tier.key === 'rising'
                            ? 'bg-sky-500/10 text-sky-700 border-sky-500/30'
                            : 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30'}`}
                      title={`${e.milestone_tier.min}+ signups reached`}
                      data-testid={`leaderboard-milestone-${e.rank}`}
                    >
                      <Trophy className="w-3 h-3" /> {e.milestone_tier.label}
                    </div>
                  )}
                  {e.pro_months_earned > 0 && (
                    <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                      <ShieldCheck className="w-3 h-3" /> {e.pro_months_earned} Pro month{e.pro_months_earned === 1 ? '' : 's'}
                    </div>
                  )}
                  {e.ambassador && !e.pro_months_earned && (
                    <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-700 border border-amber-500/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                      <Award className="w-3 h-3" /> Ambassador
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-2" role="tablist" aria-label="Filter graduates">
          {KINDS.map(({ id, label, icon: Icon }) => {
            const active = filter === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setFilter(id)}
                data-testid={`graduates-filter-${id}`}
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full h-10 px-4 text-sm font-bold transition-colors flex-shrink-0
                  ${active
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-card border border-border text-foreground/75 hover:text-foreground hover:border-primary/60'
                  }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            );
          })}
          <div className="flex-1" />
          <div className="hidden sm:block text-xs text-muted-foreground font-semibold">
            {loading ? 'Loading…' : `${counts.all} on the wall`}
          </div>
        </div>

        {loading && (
          <div className="py-24 text-center text-muted-foreground" data-testid="graduates-loading">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
            <p className="text-sm">Loading the wall…</p>
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="py-24 text-center" data-testid="graduates-empty">
            <GraduationCap className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
            <h3 className="font-heading text-xl font-extrabold text-foreground">No graduates here yet.</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Be the first to earn a certificate — the wall is waiting.
            </p>
            <Button
              onClick={() => navigate(user ? '/learning' : '/pricing')}
              className="mt-6 rounded-full font-bold bg-primary hover:bg-primary/90"
              data-testid="graduates-cta-empty"
            >
              {user ? 'Browse courses' : 'Join the Academy'}
            </Button>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="graduates-grid">
            {rows.map((g) => {
              // Match this tile against the leaderboard entry whose
              // `top_cert_number` equals this graduate's cert_number so the
              // #1 ambassador's own tile lights up when visitors land here.
              const lb = leaderboard.find((e) => e.top_cert_number === g.cert_number);
              return (
                <GraduateTile
                  key={g.cert_number}
                  grad={g}
                  API={API}
                  kudos={kudos}
                  isTopAmbassador={!!lb && lb.rank === 1}
                  ambassadorRank={lb?.rank}
                />
              );
            })}
            {rows.map((g) => (
              <GraduateTile key={g.cert_number} grad={g} API={API} kudos={kudos} />
            ))}
          </div>
        )}
      </div>

      {/* CTA footer */}
      <div className="border-t border-border bg-muted/40">
        <div className="container mx-auto px-6 py-12 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
            Your name could be up here next.
          </h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl mx-auto">
            Structured courses, live batch tracking, and business calculators — everything real operators use to level up.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Button onClick={() => navigate(user ? '/learning' : '/pricing')} className="rounded-full font-bold bg-primary hover:bg-primary/90" data-testid="graduates-footer-cta">
              {user ? 'Browse courses' : 'See membership plans'}
            </Button>
            <Button variant="outline" onClick={() => navigate('/why-us')} className="rounded-full font-bold" data-testid="graduates-footer-why">
              Why Freeze Dry Academy
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Graduates;
